<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { IconPencil, IconSchool } from '@tabler/icons-vue'
import dayjs from 'dayjs'
import { localDB } from '@/migrate.ts'
import { eventPeopleTable, eventsTable, peopleTable } from '../../../client-db/src/db/schema.ts'
import { asc, eq } from 'drizzle-orm'
import { useEventStore } from '@/stores/event-store.ts'
import { getInitials, loadPeopleUiMetaFromStorage } from '@/utils/people-avatar.ts'

type PersonOption = {
  id: number
  name: string
  birthDate: Date | null
  scopeId: number | null
  avatarUrl: string | null
}

type CountryPresetId = 'us' | 'uk' | 'canada' | 'australia' | 'india'

type CountryPreset = {
  id: CountryPresetId
  label: string
  schoolStartMonthIndex: number
  kindergartenStartAge: number
  gradeLabels: string[]
}

type EducationDraftEntry = {
  id: string
  title: string
  startDate: string
  endDate: string
}

type EducationStep = 'person' | 'birthday' | 'country' | 'review'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'created', message: string): void
}>()

const countryPresets: CountryPreset[] = [
  {
    id: 'us',
    label: 'United States (K-12)',
    schoolStartMonthIndex: 8,
    kindergartenStartAge: 5,
    gradeLabels: [
      'Kindergarten',
      '1st Grade',
      '2nd Grade',
      '3rd Grade',
      '4th Grade',
      '5th Grade',
      '6th Grade',
      '7th Grade',
      '8th Grade',
      '9th Grade (Freshman)',
      '10th Grade (Sophomore)',
      '11th Grade (Junior)',
      '12th Grade (Senior)',
    ],
  },
  {
    id: 'uk',
    label: 'United Kingdom (Reception + Years)',
    schoolStartMonthIndex: 8,
    kindergartenStartAge: 4,
    gradeLabels: [
      'Reception',
      'Year 1',
      'Year 2',
      'Year 3',
      'Year 4',
      'Year 5',
      'Year 6',
      'Year 7',
      'Year 8',
      'Year 9',
      'Year 10',
      'Year 11',
      'Year 12',
    ],
  },
  {
    id: 'canada',
    label: 'Canada (K-12)',
    schoolStartMonthIndex: 8,
    kindergartenStartAge: 5,
    gradeLabels: [
      'Kindergarten',
      'Grade 1',
      'Grade 2',
      'Grade 3',
      'Grade 4',
      'Grade 5',
      'Grade 6',
      'Grade 7',
      'Grade 8',
      'Grade 9',
      'Grade 10',
      'Grade 11',
      'Grade 12',
    ],
  },
  {
    id: 'australia',
    label: 'Australia (Prep + Years)',
    schoolStartMonthIndex: 0,
    kindergartenStartAge: 5,
    gradeLabels: [
      'Prep/Foundation',
      'Year 1',
      'Year 2',
      'Year 3',
      'Year 4',
      'Year 5',
      'Year 6',
      'Year 7',
      'Year 8',
      'Year 9',
      'Year 10',
      'Year 11',
      'Year 12',
    ],
  },
  {
    id: 'india',
    label: 'India (Kindergarten + Classes)',
    schoolStartMonthIndex: 5,
    kindergartenStartAge: 5,
    gradeLabels: [
      'Kindergarten',
      'Class 1',
      'Class 2',
      'Class 3',
      'Class 4',
      'Class 5',
      'Class 6',
      'Class 7',
      'Class 8',
      'Class 9',
      'Class 10',
      'Class 11',
      'Class 12',
    ],
  },
]

const educationStep = ref<EducationStep>('person')
const educationPeople = ref<PersonOption[]>([])
const selectedPersonId = ref<number | null>(null)
const birthdayInput = ref('')
const selectedCountryPresetId = ref<CountryPresetId>('us')
const educationDraft = ref<EducationDraftEntry[]>([])
const wizardError = ref<string | null>(null)
const loadingEducationPeople = ref(false)
const savingWizard = ref(false)
const failedAvatarIds = ref<number[]>([])
const globalStartDateInput = ref('')
const globalEndDateInput = ref('')
const editingEducationEntryIds = ref<string[]>([])

const eventStore = useEventStore()

const selectedPerson = computed(() => {
  if (!selectedPersonId.value) return null
  return educationPeople.value.find((person) => person.id === selectedPersonId.value) ?? null
})

const selectedCountryPreset = computed(() => {
  return (
    countryPresets.find((preset) => preset.id === selectedCountryPresetId.value) ??
    countryPresets[0]
  )
})

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      void initializeWizard()
    }
  },
)

function formatDateInput(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return ''
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseDateInput(value: string): Date | null {
  if (!value.trim()) return null
  const parsed = new Date(`${value}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDisplayDate(value: string): string {
  if (!value.trim()) return ''
  const parsed = dayjs(`${value}T00:00:00`)
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value
}

function addYearsToDate(date: Date, yearsToAdd: number): Date {
  const next = new Date(date)
  next.setFullYear(next.getFullYear() + yearsToAdd)
  return next
}

function hasAvatar(person: PersonOption) {
  return Boolean(person.avatarUrl) && !failedAvatarIds.value.includes(person.id)
}

function markAvatarAsFailed(personId: number) {
  if (!failedAvatarIds.value.includes(personId)) {
    failedAvatarIds.value = [...failedAvatarIds.value, personId]
  }
}

async function loadEducationPeople() {
  loadingEducationPeople.value = true
  wizardError.value = null

  try {
    const peopleUiMeta = loadPeopleUiMetaFromStorage()
    const rows = await localDB
      .select({
        id: peopleTable.id,
        fullName: peopleTable.fullName,
        firstName: peopleTable.firstName,
        lastName: peopleTable.lastName,
        birthDate: peopleTable.birthDate,
        scopeId: peopleTable.scopeId,
      })
      .from(peopleTable)
      .orderBy(asc(peopleTable.fullName))

    educationPeople.value = rows.map((person) => {
      const fallbackName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
      return {
        id: person.id,
        name: person.fullName || fallbackName || `Person ${person.id}`,
        birthDate: person.birthDate ?? null,
        scopeId: person.scopeId ?? null,
        avatarUrl: peopleUiMeta[String(person.id)]?.avatarUrl ?? null,
      }
    })
  } catch (error) {
    console.error('Failed to load people for education wizard:', error)
    wizardError.value = 'Failed to load people.'
  } finally {
    loadingEducationPeople.value = false
  }
}

async function initializeWizard() {
  educationStep.value = 'person'
  selectedPersonId.value = null
  birthdayInput.value = ''
  selectedCountryPresetId.value = 'us'
  educationDraft.value = []
  globalStartDateInput.value = ''
  globalEndDateInput.value = ''
  editingEducationEntryIds.value = []
  failedAvatarIds.value = []
  wizardError.value = null
  await loadEducationPeople()
}

function closeEducationWizard() {
  if (savingWizard.value) return
  emit('update:open', false)
}

function getBirthdayForWizard(): Date | null {
  if (birthdayInput.value.trim()) {
    return parseDateInput(birthdayInput.value)
  }
  return selectedPerson.value?.birthDate ?? null
}

function buildEducationDraft(birthDate: Date, preset: CountryPreset): EducationDraftEntry[] {
  return preset.gradeLabels.map((gradeLabel, index) => {
    const startYear = birthDate.getFullYear() + preset.kindergartenStartAge + index
    const startDate = new Date(startYear, preset.schoolStartMonthIndex, 1)
    const endDate = new Date(startDate.getFullYear() + 1, preset.schoolStartMonthIndex, 0)

    return {
      id: `education-${index}`,
      title: gradeLabel,
      startDate: toIsoDate(startDate),
      endDate: toIsoDate(endDate),
    }
  })
}

async function moveEducationWizardForward() {
  wizardError.value = null

  if (educationStep.value === 'person') {
    if (!selectedPerson.value) {
      wizardError.value = 'Select a person to continue.'
      return
    }

    birthdayInput.value = formatDateInput(selectedPerson.value.birthDate)
    educationStep.value = 'birthday'
    return
  }

  if (educationStep.value === 'birthday') {
    const parsedBirthday = parseDateInput(birthdayInput.value)
    if (!parsedBirthday) {
      wizardError.value = 'Enter a valid birthday.'
      return
    }

    try {
      await localDB
        .update(peopleTable)
        .set({ birthDate: parsedBirthday })
        .where(eq(peopleTable.id, selectedPerson.value!.id))

      const person = selectedPerson.value
      if (person) {
        person.birthDate = parsedBirthday
      }
      educationStep.value = 'country'
    } catch (error) {
      console.error('Failed to save birthday for education wizard:', error)
      wizardError.value = 'Failed to save birthday.'
    }
    return
  }

  if (educationStep.value === 'country') {
    const birthDate = getBirthdayForWizard()
    if (!birthDate) {
      wizardError.value = 'Birthday is required before generating the timeline.'
      return
    }

    educationDraft.value = buildEducationDraft(birthDate, selectedCountryPreset.value)
    globalStartDateInput.value = educationDraft.value[0]?.startDate ?? ''
    globalEndDateInput.value = educationDraft.value[0]?.endDate ?? ''
    editingEducationEntryIds.value = []
    educationStep.value = 'review'
  }
}

function moveEducationWizardBack() {
  wizardError.value = null

  if (educationStep.value === 'birthday') {
    educationStep.value = 'person'
    return
  }

  if (educationStep.value === 'country') {
    educationStep.value = 'birthday'
    return
  }

  if (educationStep.value === 'review') {
    educationStep.value = 'country'
  }
}

async function createEducationEvents() {
  if (!selectedPerson.value) {
    wizardError.value = 'Select a person to continue.'
    return
  }

  if (educationDraft.value.length === 0) {
    wizardError.value = 'No timeline rows to create.'
    return
  }

  const invalidEntry = educationDraft.value.find((entry) => {
    const start = parseDateInput(entry.startDate)
    const end = parseDateInput(entry.endDate)
    return !entry.title.trim() || !start || !end || start > end
  })

  if (invalidEntry) {
    wizardError.value = 'Every row needs a title and valid start/end dates.'
    return
  }

  savingWizard.value = true
  wizardError.value = null

  try {
    for (const entry of educationDraft.value) {
      const startDate = parseDateInput(entry.startDate)
      const endDate = parseDateInput(entry.endDate)
      if (!startDate || !endDate) continue

      const [createdEvent] = await localDB
        .insert(eventsTable)
        .values({
          scopeId: selectedPerson.value.scopeId ?? null,
          title: entry.title.trim(),
          description: `${selectedPerson.value.name} - ${entry.title.trim()}`,
          text: `Education timeline (${selectedCountryPreset.value.label})`,
          date: startDate,
          endDate,
        })
        .returning({ id: eventsTable.id })

      if (createdEvent?.id) {
        await localDB.insert(eventPeopleTable).values({
          eventId: createdEvent.id,
          personId: selectedPerson.value.id,
          relevance: 1,
        })
      }
    }

    await eventStore.getAllEvents()
    emit(
      'created',
      `Created ${educationDraft.value.length} education events for ${selectedPerson.value.name}.`,
    )
    emit('update:open', false)
  } catch (error) {
    console.error('Failed creating education events:', error)
    wizardError.value = 'Failed to create education events.'
  } finally {
    savingWizard.value = false
  }
}

function applyGlobalDatesToDraft() {
  const globalStartDate = parseDateInput(globalStartDateInput.value)
  const globalEndDate = parseDateInput(globalEndDateInput.value)

  if (!globalStartDate || !globalEndDate) {
    wizardError.value = 'Set valid global start and end dates first.'
    return
  }

  if (globalStartDate > globalEndDate) {
    wizardError.value = 'Global start date must be on or before global end date.'
    return
  }

  wizardError.value = null
  educationDraft.value = educationDraft.value.map((entry, index) => ({
    ...entry,
    startDate: toIsoDate(addYearsToDate(globalStartDate, index)),
    endDate: toIsoDate(addYearsToDate(globalEndDate, index)),
  }))
}

function jumpToEducationStep(targetStep: EducationStep) {
  if (savingWizard.value) return
  wizardError.value = null

  if (targetStep === 'person') {
    educationStep.value = 'person'
    return
  }

  if (!selectedPerson.value) {
    wizardError.value = 'Select a person first.'
    educationStep.value = 'person'
    return
  }

  if (!birthdayInput.value) {
    birthdayInput.value = formatDateInput(selectedPerson.value.birthDate)
  }

  if (targetStep === 'birthday') {
    educationStep.value = 'birthday'
    return
  }

  const hasBirthday =
    Boolean(selectedPerson.value.birthDate) || Boolean(parseDateInput(birthdayInput.value))
  if (!hasBirthday) {
    wizardError.value = 'Set a valid birthday before choosing a school system.'
    educationStep.value = 'birthday'
    return
  }

  if (targetStep === 'country') {
    educationStep.value = 'country'
    return
  }

  const birthDate = getBirthdayForWizard()
  if (!birthDate) {
    wizardError.value = 'Birthday is required before reviewing the timeline.'
    educationStep.value = 'birthday'
    return
  }

  if (educationDraft.value.length === 0) {
    educationDraft.value = buildEducationDraft(birthDate, selectedCountryPreset.value)
    globalStartDateInput.value = educationDraft.value[0]?.startDate ?? ''
    globalEndDateInput.value = educationDraft.value[0]?.endDate ?? ''
    editingEducationEntryIds.value = []
  }

  educationStep.value = 'review'
}

function isEducationEntryEditing(entryId: string): boolean {
  return editingEducationEntryIds.value.includes(entryId)
}

function toggleEducationEntryEditing(entryId: string): void {
  if (isEducationEntryEditing(entryId)) {
    editingEducationEntryIds.value = editingEducationEntryIds.value.filter((id) => id !== entryId)
    return
  }
  editingEducationEntryIds.value = [...editingEducationEntryIds.value, entryId]
}
</script>

<template>
  <div class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': open }">
    <div
      class="modal-box w-full max-w-none sm:max-w-4xl h-[85dvh] sm:h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
    >
      <h3 class="text-lg font-semibold">Education Wizard</h3>
      <p class="mt-1 text-sm text-base-content/70">
        Scaffold out an education timeline for
        {{ selectedPerson?.name ? selectedPerson?.name : 'a person' }}, edit, and save it to their
        timeline.
      </p>

      <ul class="steps steps-horizontal w-full mt-4 mb-5">
        <li
          class="step cursor-pointer"
          :class="educationStep === 'person' ? 'step-primary' : ''"
          @click="jumpToEducationStep('person')"
        >
          Person
        </li>
        <li
          class="step cursor-pointer"
          :class="educationStep === 'birthday' ? 'step-primary' : ''"
          @click="jumpToEducationStep('birthday')"
        >
          Birthday
        </li>
        <li
          class="step cursor-pointer"
          :class="educationStep === 'country' ? 'step-primary' : ''"
          @click="jumpToEducationStep('country')"
        >
          Country
        </li>
        <li
          class="step cursor-pointer"
          :class="educationStep === 'review' ? 'step-primary' : ''"
          @click="jumpToEducationStep('review')"
        >
          Review
        </li>
      </ul>

      <div
        v-if="wizardError"
        class="mb-3 rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
      >
        {{ wizardError }}
      </div>

      <template v-if="educationStep === 'person'">
        <p class="text-sm mb-2">Who is this timeline for?</p>
        <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-64 overflow-y-auto pr-1">
          <button
            v-for="person in educationPeople"
            :key="person.id"
            type="button"
            class="group w-full text-center"
            :class="selectedPersonId === person.id ? 'opacity-100' : 'opacity-90 hover:opacity-100'"
            @click="selectedPersonId = person.id"
          >
            <span
              class="mx-auto mb-1 inline-flex h-14 w-14 items-center justify-center rounded-full border-2 bg-base-200 text-xs font-semibold overflow-hidden transition-colors"
              :class="
                selectedPersonId === person.id
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-base-300'
              "
            >
              <img
                v-if="hasAvatar(person)"
                :src="person.avatarUrl || ''"
                :alt="`${person.name} avatar`"
                class="h-full w-full object-cover"
                @error="markAvatarAsFailed(person.id)"
              />
              <span v-else>{{ getInitials(person.name) }}</span>
            </span>
            <span class="block truncate text-[11px] leading-tight">{{ person.name }}</span>
          </button>
        </div>
        <p v-if="loadingEducationPeople" class="mt-2 text-sm text-base-content/70">Loading people...</p>
        <p v-else-if="educationPeople.length === 0" class="mt-2 text-sm text-base-content/70">
          No people found. Add someone in the People tab first.
        </p>
      </template>

      <template v-else-if="educationStep === 'birthday'">
        <p class="text-sm text-base-content/75 mb-2">
          {{ selectedPerson?.name }} does not have a birthday set yet.
        </p>
        <label class="form-control w-full max-w-sm">
          <span class="label-text mb-1">Birthday</span>
          <input
            v-model="birthdayInput"
            type="date"
            class="input input-bordered w-full bg-base-100 text-base-content"
          />
        </label>
      </template>

      <template v-else-if="educationStep === 'country'">
        <label class="form-control w-full max-w-xl">
          <span class="label-text mb-1">Primary school country/system</span>
          <select
            v-model="selectedCountryPresetId"
            class="select select-bordered w-full bg-base-100 text-base-content"
          >
            <option v-for="country in countryPresets" :key="country.id" :value="country.id">
              {{ country.label }}
            </option>
          </select>
        </label>
        <p class="mt-2 text-sm text-base-content/70">
          We'll scaffold dates using a typical start age and school-year calendar for this system.
        </p>
      </template>

      <template v-else>
        <div class="mb-4 rounded-xl border border-base-300 bg-base-100 p-3">
          <div class="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">Default Dates</div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label class="form-control">
              <span class="label-text text-xs">School year 1 start</span>
              <input
                v-model="globalStartDateInput"
                type="date"
                class="input input-bordered w-full bg-base-100 text-base-content"
              />
            </label>
            <label class="form-control">
              <span class="label-text text-xs">School year 1 end</span>
              <input
                v-model="globalEndDateInput"
                type="date"
                class="input input-bordered w-full bg-base-100 text-base-content"
              />
            </label>
            <div class="flex items-end">
              <button type="button" class="btn btn-outline w-full" @click="applyGlobalDatesToDraft">
                Apply to all years
              </button>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-base-300">
          <ul class="timeline timeline-vertical timeline-compact p-4 education-timeline">
            <li v-for="entry in educationDraft" :key="entry.id">
              <hr class="bg-primary/30" />
              <div class="timeline-middle">
                <IconSchool class="h-5 w-5 text-primary" />
              </div>
              <div class="timeline-end timeline-box w-full text-start">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="font-medium">{{ entry.title }}</div>
                    <div class="text-sm text-base-content/70">
                      {{ formatDisplayDate(entry.startDate) }} to {{ formatDisplayDate(entry.endDate) }}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm btn-square"
                    :aria-label="
                      isEducationEntryEditing(entry.id)
                        ? `Hide edit fields for ${entry.title}`
                        : `Edit ${entry.title}`
                    "
                    @click="toggleEducationEntryEditing(entry.id)"
                  >
                    <IconPencil class="h-4 w-4" />
                  </button>
                </div>
                <div v-if="isEducationEntryEditing(entry.id)" class="mt-3 grid grid-cols-1 md:grid-cols-1 gap-2">
                  <input
                    v-model="entry.title"
                    type="text"
                    class="input input-bordered w-full bg-base-100 text-base-content"
                    placeholder="Title"
                  />
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label class="form-control">
                      <span class="label-text text-xs">Start date</span>
                      <input
                        v-model="entry.startDate"
                        type="date"
                        class="input input-bordered w-full bg-base-100 text-base-content"
                      />
                    </label>
                    <label class="form-control">
                      <span class="label-text text-xs">End date</span>
                      <input
                        v-model="entry.endDate"
                        type="date"
                        class="input input-bordered w-full bg-base-100 text-base-content"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <hr class="bg-primary/30" />
            </li>
          </ul>
        </div>
        <p class="mt-2 text-sm text-base-content/70">
          Click the edit icon on any year to change its title or dates before creating events.
        </p>
      </template>

      <div class="modal-action">
        <button class="btn" :disabled="savingWizard" @click="closeEducationWizard">Cancel</button>
        <button
          v-if="educationStep !== 'person'"
          class="btn btn-outline"
          :disabled="savingWizard"
          @click="moveEducationWizardBack"
        >
          Back
        </button>
        <button
          v-if="educationStep !== 'review'"
          class="btn btn-primary"
          :disabled="savingWizard"
          @click="moveEducationWizardForward"
        >
          Next
        </button>
        <button
          v-else
          class="btn btn-primary"
          :disabled="savingWizard"
          @click="createEducationEvents"
        >
          {{ savingWizard ? 'Creating...' : 'Create events' }}
        </button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeEducationWizard">
      <button>close</button>
    </div>
  </div>
</template>

<style scoped>
.education-timeline {
  --timeline-col-start: 0;
}

.education-timeline .timeline-middle {
  justify-self: start;
}

.education-timeline .timeline-end {
  justify-self: start;
  width: 100%;
}
</style>
